import { useFormik } from 'formik';
import { ChangeEvent, useEffect, useState } from 'react';
import usePatchGroup from '../../../../API/hooks/groups/usePatchGroup';
import { PatchGroupRequest, Group } from '../../../../API/types/Groups';
import Input from '../../../../components/common/Input/Input';
import { Button } from '../../../../components/ui/Button/Button';
import S from './EditGroupStyles';

type Props = {
  data: Group | undefined;
  toggleModal: (closed: boolean) => void;
};

function EditGroup({ data, toggleModal }: Props): JSX.Element {
  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const onSubmitSuccess = (): void => toggleModal(false);
  const onSubmitFail = (errorMessage: string): void => setSubmitError(errorMessage);

  const { mutate } = usePatchGroup(onSubmitSuccess, onSubmitFail);

  const formik = useFormik({
    initialValues: {
      name: data?.name || '',
    },
    onSubmit: (values) => mutate({
      ...values,
      id: data?._id || '',
      fleetId: data?.fleetId || '',
    } as unknown as PatchGroupRequest),
    validate: (values) => {
      const errors = {} as Record<string, string | undefined>;

      if (!values.name.length) errors.name = 'Required';
      return errors;
    },
    validateOnMount: true,
  });

  // if there is onSubmit error formik will stack in submitting state
  // blocking further interactions with submit button
  useEffect(() => {
    formik.setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  const handleInputChange = (e: ChangeEvent<any>): void => {
    formik.handleChange(e);
    setSubmitError('');
  };

  return (
    <S.Wrapper>
      <form onSubmit={formik.handleSubmit}>
        <div className="details-container">
          <S.RowContainer>
            <Input
              label="Name"
              name="name"
              placeholder="Type group name"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              value={formik.values.name}
              error={formik.touched.name && formik.errors.name}
            />
          </S.RowContainer>
          <S.RowContainer className="buttons-container">
            <Button
              height="50px"
              onClick={() => toggleModal(false)}
              type="button"
            >
              Cancel
            </Button>
            <Button
              className="add-button"
              height="50px"
              type="submit"
              disabled={formik.isSubmitting || !formik.isValid || !!submitError}
            >
              Save
            </Button>
          </S.RowContainer>
          {!!submitError && (
            typeof submitError === 'string'
              ? <S.ErrorMessage>{submitError}</S.ErrorMessage>
              : submitError.map((error) => <S.ErrorMessage>{error}</S.ErrorMessage>)
          )}
        </div>
      </form>
    </S.Wrapper>
  );
}

export default EditGroup;
